import * as Immutable from 'seamless-immutable';
import isNullOrUndefined from '@adsk/bim360-matrix-react-helpers/es/isNullOrUndefined';
import lodashPick from 'lodash/pick';
import i18n from '../../utils/i18n';

import {
  ISSUE_TYPES_FETCH_START,
  ISSUE_TYPES_FETCH_API_FULFILLED,
  ISSUE_TYPES_FETCH_API_REJECTED,
  ISSUE_TYPES_CREATE_API_FULFILLED,
  ISSUE_TYPES_CREATE_START,
  ISSUE_TYPES_CREATE_API_REJECTED,
  ISSUE_SUB_TYPES_CREATE_API_FULFILLED,
  ISSUE_TYPES_UPDATE_STATUS_API_FULFILLED,
  ISSUE_TYPES_UPDATE_TITLE_API_FULFILLED,
  ISSUE_SUB_TYPES_CREATE_START,
  ISSUE_SUB_TYPES_CREATE_API_REJECTED,
  ISSUE_TYPES_UPDATE_TITLE_START,
  ISSUE_TYPES_SELECT_FOR_EDIT,
  ISSUE_TYPES_UPDATE_API_PENDING,
  ISSUE_TYPES_UPDATE_API_FULFILLED,
  ISSUE_TYPES_UPDATE_API_REJECTED,
  ISSUE_TYPES_SET_SELECTED_ATTRIBUTES,
  SHOW_ISSUE_TYPES_EDIT_MODAL,
  ISSUE_TYPES_SET_ATTRIBUTE_MAPPINGS,
  SHOW_ISSUE_TYPES_ADD_ATTRIBUTES_MODAL,
  SHOW_ISSUE_TYPES_LEAVE_ADD_ATTRIBUTES_MODAL,
  ISSUE_TYPES_PENDING_UPDATE,
  ISSUE_TYPES_SET_SELECTED_ATTRIBUTE_MAPPING_REQUIRED
} from '../actions';

import {sortAlphabeticallyByTitle, sortByOrder} from '../../utils/sortingHelpers';

const initialState = Immutable.from({
  loading: false,
  error: '',
  isCreating: false,
  containerId: null,
  entitiesMap: {},
  issueTypeIds: [],
  selectedIssueTypeForEdit: null,
  pendingIssueType: null,
  selectedIssueTypeAttributes: [],
  isUpdating: false,
  fetched: false,
  editModalOpen: false,
  addAttributesModalOpen: false,
  leaveAddAttributesModalOpen: false
});

const flattenIssueTypes = (
  entitiesMap,
  shouldSortIssueTypes = false,
  shouldSortSubTypes = false,
  oldEntities = null,
  propertiesToCopy = []
) =>
  entitiesMap.reduce((acc, issueType, orderIndex) => {
    acc[issueType.id] = issueType;
    acc[issueType.id].mappingIds = issueType.mappingIds || [];
    if (shouldSortIssueTypes) {
      issueType.orderIndex = orderIndex;
    }
    if (issueType.subtypes) {
      issueType.childrenIds = issueType.subtypes.sort(sortAlphabeticallyByTitle).map((subType, index) => {
        acc[subType.id] = subType;
        acc[subType.id].mappingIds = subType.mappingIds || [];
        acc[subType.id].parentId = issueType.id;
        if (shouldSortSubTypes) {
          acc[subType.id].orderIndex = index;
        }
        if (!!oldEntities && propertiesToCopy.length > 0) {
          const oldProperties = lodashPick(oldEntities[subType.id], propertiesToCopy);
          acc[subType.id] = {...acc[subType.id], ...oldProperties};
        }
        return subType.id;
      });
      const immediateSubType = issueType.subtypes.find(subtype => subtype.title === issueType.title);
      issueType.immediateSubTypeId = immediateSubType ? immediateSubType.id : null;
    }
    delete issueType.subtypes;
    return acc;
  }, {});

const getIds = entitiesMap => entitiesMap.map(issueType => issueType.id);

const updateTypesStatus = (entitiesMap, issueTypesToUpdate, status) => {
  const updatedEntites = {};
  issueTypesToUpdate.forEach(issueTypeId => {
    updatedEntites[issueTypeId] = {...entitiesMap[issueTypeId], isActive: status};
  });
  return updatedEntites;
};

const updateIssueTypeStatus = (state, action) => {
  const parentId = action.meta && action.meta.parentId;
  const issueTypeId = action.payload.id;
  const issueType = state.entitiesMap[issueTypeId];
  const newActiveStatus = action.meta.isActive;
  if (parentId) {
    const parent = state.entitiesMap[parentId];
    // If the parent is inactive then we need to update the parent + the immediate subType to active alongside the required issueSubType
    if (parent.isActive === false && newActiveStatus === true) {
      const updatedEntities = updateTypesStatus(
        state.entitiesMap,
        [parentId, parent.immediateSubTypeId, issueTypeId],
        newActiveStatus
      );
      return state.setIn(['entitiesMap'], state.entitiesMap.merge(updatedEntities));
    }
    return state.setIn(['entitiesMap', issueTypeId, 'isActive'], newActiveStatus);
  }
  return state.setIn(
    ['entitiesMap'],
    state.entitiesMap.merge(
      flattenIssueTypes(
        [{...action.payload, orderIndex: issueType.orderIndex, mappingIds: issueType.mappingIds}],
        false,
        true,
        state.entitiesMap,
        ['mappingIds']
      )
    )
  );
};

const updateIssueTypeTitle = (state, action) => {
  const issueType = state.entitiesMap[action.payload.id];
  if (issueType.parentId) {
    return state.setIn(
      ['entitiesMap'],
      state.entitiesMap.merge({
        [action.payload.id]: {
          ...action.payload,
          orderIndex: issueType.orderIndex,
          parentId: issueType.parentId,
          mappingIds: issueType.mappingIds
        }
      })
    );
  }
  return state.setIn(
    ['entitiesMap'],
    state.entitiesMap.merge(
      flattenIssueTypes(
        [{...action.payload, orderIndex: issueType.orderIndex, mappingIds: issueType.mappingIds}],
        false,
        true,
        state.entitiesMap,
        ['mappingIds']
      )
    )
  );
};

export default (state = initialState, action) => {
  switch (action.type) {
    case ISSUE_TYPES_FETCH_START:
      return state.merge({loading: true, error: ''});
    case ISSUE_TYPES_FETCH_API_FULFILLED: {
      const newState = state.merge({loading: false, error: '', fetched: true});
      const containerIssueType = {
        id: action.meta.containerId,
        childrenIds: [],
        title: i18n.t('issueTypes.allIssueTypes'),
        isActive: true,
        isContainerType: true,
        permittedActions: [],
        permittedAttributes: []
      };
      return newState.merge({
        issueTypeIds: getIds([containerIssueType, ...action.payload]),
        entitiesMap: flattenIssueTypes(
          [containerIssueType, ...action.payload.sort(sortAlphabeticallyByTitle)],
          true,
          true
        )
      });
    }
    case ISSUE_TYPES_FETCH_API_REJECTED:
      return state.merge({error: action.payload.message || 'ISSUE TYPES FETCH Error', loading: false});
    case ISSUE_TYPES_CREATE_START:
      return state.merge({isCreating: true});
    case ISSUE_TYPES_CREATE_API_FULFILLED: {
      const containerMappings = state.entitiesMap[action.meta.containerId]
        ? state.entitiesMap[action.meta.containerId].mappingIds
        : [];
      const subtypes = (action.payload.subtypes || []).map(subtype => ({
        ...subtype,
        mappingIds: containerMappings
      }));
      return state.merge({
        entitiesMap: state.entitiesMap.merge(
          flattenIssueTypes([
            {
              ...action.payload,
              subtypes,
              orderIndex: state.issueTypeIds.length,
              mappingIds: containerMappings
            }
          ])
        ),
        issueTypeIds: state.issueTypeIds.concat(action.payload.id),
        isCreating: false
      });
    }
    case ISSUE_TYPES_CREATE_API_REJECTED:
      return state.merge({isCreating: false});
    case ISSUE_SUB_TYPES_CREATE_START: {
      const parentId = action.payload.parentId;
      const subTypeId = action.payload.clientID;
      const newState = state.setIn(
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.concat(subTypeId)
      );
      const newSubType = {
        ...action.payload,
        id: subTypeId,
        orderIndex: newState.entitiesMap[parentId].childrenIds.length,
        parentId
      };
      return newState.setIn(['entitiesMap', subTypeId], newSubType);
    }
    case ISSUE_SUB_TYPES_CREATE_API_FULFILLED: {
      const parentId = action.meta && action.meta.parentId;
      const tempId = action.meta && action.meta.clientID;
      let newState = state.setIn(
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.filter(id => id !== tempId).concat(action.payload.id)
      );
      const newSubType = {
        ...action.payload,
        orderIndex: newState.entitiesMap[parentId].childrenIds.length,
        parentId,
        mappingIds: newState.entitiesMap[parentId].mappingIds
      };
      newState = newState.merge({entitiesMap: newState.entitiesMap.without(tempId)});
      return newState.setIn(['entitiesMap', [newSubType.id]], newSubType);
    }
    case ISSUE_SUB_TYPES_CREATE_API_REJECTED: {
      const parentId = action.meta && action.meta.parentId;
      const tempId = action.meta && action.meta.clientID;
      const newState = state.setIn(
        ['entitiesMap', parentId, 'childrenIds'],
        state.entitiesMap[parentId].childrenIds.filter(id => id !== tempId)
      );
      return newState.merge({entitiesMap: newState.entitiesMap.without(tempId)});
    }
    case ISSUE_TYPES_UPDATE_STATUS_API_FULFILLED: {
      return updateIssueTypeStatus(state, action);
    }
    case ISSUE_TYPES_UPDATE_TITLE_START: {
      const issueTypeId = action.payload.id;
      const newTitle = action.payload.title;
      const immediateSubTypeId = state.entitiesMap[issueTypeId].immediateSubTypeId;
      let newState = state.setIn(['entitiesMap', issueTypeId, 'title'], newTitle);
      if (immediateSubTypeId) {
        newState = newState.setIn(['entitiesMap', immediateSubTypeId, 'title'], newTitle);
      }
      return newState;
    }
    case ISSUE_TYPES_UPDATE_TITLE_API_FULFILLED: {
      return updateIssueTypeTitle(state, action);
    }

    case ISSUE_TYPES_SELECT_FOR_EDIT: {
      return state.merge({selectedIssueTypeForEdit: action.payload.id, pendingIssueType: {}});
    }

    case ISSUE_TYPES_UPDATE_API_PENDING: {
      return state.merge({isUpdating: true});
    }

    case ISSUE_TYPES_UPDATE_API_FULFILLED: {
      let newState = state.merge({isUpdating: false});
      if (!isNullOrUndefined(action.meta.title)) {
        newState = updateIssueTypeTitle(newState, action);
      }
      if (!isNullOrUndefined(action.meta.isActive)) {
        newState = updateIssueTypeStatus(newState, action);
      }
      return newState;
    }

    case ISSUE_TYPES_UPDATE_API_REJECTED: {
      return state.merge({isUpdating: false});
    }

    case ISSUE_TYPES_SET_ATTRIBUTE_MAPPINGS: {
      if (state.fetched) {
        const results = action.payload;
        const containerId = action.meta.containerId;
        let newState = state;

        const containerMappings = results
          .filter(mapping => mapping.mappedItemId === containerId)
          .sort(sortByOrder)
          .map(mapping => ({
            id: mapping.id,
            attributeDefinitionId: mapping.attributeDefinitionId
          }));
        const containerMappingIds = containerMappings.map(m => m.id);
        const containerDefinitions = containerMappings.map(m => m.attributeDefinitionId);

        newState = newState.setIn(['entitiesMap', containerId, 'mappingIds'], containerMappingIds);

        state.issueTypeIds
          .filter(id => id !== containerId)
          .forEach(typeId => {
            let usedDefinitions = [].concat(containerDefinitions);
            const issueTypeMappings = results
              .filter(
                mapping => mapping.mappedItemId === typeId && !usedDefinitions.includes(mapping.attributeDefinitionId)
              )
              .sort(sortByOrder)
              .map(mapping => ({
                id: mapping.id,
                attributeDefinitionId: mapping.attributeDefinitionId
              }));

            usedDefinitions = usedDefinitions.concat(issueTypeMappings.map(m => m.attributeDefinitionId));
            const issueTypeMappingIds = containerMappingIds.concat(issueTypeMappings.map(m => m.id));

            state.entitiesMap[typeId].childrenIds.forEach(subTypeId => {
              const issueSubTypeMappings = issueTypeMappingIds.concat(
                results
                  .filter(
                    mapping =>
                      mapping.mappedItemId === subTypeId && !usedDefinitions.includes(mapping.attributeDefinitionId)
                  )
                  .sort(sortByOrder)
                  .map(mapping => mapping.id)
              );
              newState = newState.setIn(['entitiesMap', subTypeId, 'mappingIds'], issueSubTypeMappings);
            });
            newState = newState.setIn(['entitiesMap', typeId, 'mappingIds'], issueTypeMappingIds);
          });
        return newState;
      }
      return state;
    }

    case ISSUE_TYPES_SET_SELECTED_ATTRIBUTES:
      return state.merge({selectedIssueTypeAttributes: action.payload.attributes});

    case ISSUE_TYPES_SET_SELECTED_ATTRIBUTE_MAPPING_REQUIRED: {
      const {isRequired, mapping} = action.payload;
      const newMappings = state.selectedIssueTypeAttributes.map(m =>
        m.attributeDefinitionId === mapping.attributeDefinitionId ? {...m, isRequired} : m
      );
      return state.merge({selectedIssueTypeAttributes: newMappings});
    }

    case SHOW_ISSUE_TYPES_EDIT_MODAL:
      return state.merge({editModalOpen: action.payload.visible});

    case SHOW_ISSUE_TYPES_ADD_ATTRIBUTES_MODAL:
      return state.merge({addAttributesModalOpen: action.payload.visible});

    case SHOW_ISSUE_TYPES_LEAVE_ADD_ATTRIBUTES_MODAL:
      return state.merge({leaveAddAttributesModalOpen: action.payload.visible});

    case ISSUE_TYPES_PENDING_UPDATE:
      return state.setIn(['pendingIssueType'], state.pendingIssueType.merge({...action.payload}));

    default:
      return state;
  }
};
